import { Box, Grid, Typography } from '@material-ui/core';
import { debounce, formatDate } from '@roc/feature-utils';
import { Button, DialogState, NumericAutocompleteField } from '@roc/ui';
import {
  createDateField,
  createHtmlField,
  createNumberFormatField,
  createSelectField,
  createTextField,
} from '@roc/ui/componentBuilder';
import { observer } from 'mobx-react';
import { useGeneralTasksStore } from '../../../hooks/useGeneralTasksStore';
import {
  appraisalTaskTypes,
  generalTaskPriorityOptions,
  generalTaskStatusEditOptions,
  lineItemsTaskTypes,
  taskTypeOptions,
  TECH_SUPPORT_ID,
} from '../../../utils/generalTasksConstants';
import { Attachment } from '../attachment';
import { RelatedTask } from '../relatedTask';
import { TaskAssigneeField } from '../taskAssigneeField';
import { TaskStatusField } from '../taskStatusField';
import { TaskPriorityField } from '../taskPriorityField';
import { TaskWatchField } from '../taskWatchField';
import { useUserStore } from '@roc/feature-app-core';
import { useEffect, useMemo, useState } from 'react';
import { TaskTypeForm } from '../../../types/generalTasksTypes';


export const TaskDetails = observer(() => {
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore, supportTasksStore, globalStore } = generalTasksStore;
  const {
    attachments,
    comments,
    attachmentStore,
    commentStore,
    dialogState,
    isReadOnly,
    isSubTask,
    task,
    isGeneralTask
  } = editTaskStore;
  const store = editTaskStore;
  const isLoanTask = !!task?.attributes?.loanTaskId;

  return (
    <Grid container spacing={2}>
      {
        isSubTask &&
        <Grid item xs={12}>
          <Box pb={2}>
            <Typography variant="h6">Parent Task</Typography>
          </Box>
          <Box pt={2}>
            <Typography variant="h6">Task Details</Typography>
          </Box>
        </Grid>
      }
      <Grid item xs={12}>
        {createTextField({
          store,
          testId: 'title',
          fieldName: 'title',
          label: 'Title',
          disabled: isReadOnly,
        })}
      </Grid>

      <Grid item xs={12}>
        {createHtmlField({
          store,
          testId: 'description',
          fieldName: 'description',
          label: 'Description',
          multiline: true,
          minRows: 5,
          disabled: isReadOnly,
        })}
      </Grid>
      {DialogState.EDIT === dialogState && !globalStore.isLoanChatApp && (
        <>
          {!isSubTask && !isLoanTask && !supportTasksStore.openModal && isGeneralTask && (
            <>
              <Grid item xs={12}>
                <Box pt={2}>
                  <Typography variant="h6">Subtasks</Typography>
                </Box>
                {editTaskStore.task?.subtasks?.map(subtask => (
                  <Box pt={2}>
                    <RelatedTask taskInfo={subtask} />
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Button
                  testId="add-attachment"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    editTaskStore.openAddSubtask(generalTasksStore.loanId);
                  }}
                >
                  Add Subtask
                </Button>
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  );
});

export const AttributesPanel = observer(() => {
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore, assigneeOptions, supportTasksStore, watcherOptions } = generalTasksStore;
  const isLoanView = !!generalTasksStore.loanId;
  const { isReadOnly, taskV2, dialogState, isEscalationTask, isGeneralTask } = editTaskStore;
  const store = editTaskStore;
  const assigneeStartDate = taskV2?.taskAssignees?.[0]?.createdDate;
  const { openModal } = supportTasksStore;

  useEffect(() => {
    editTaskStore.onFieldChange("assigneeRequired", !openModal);
  }, [openModal, editTaskStore]);

  useEffect(() => {
    store.isReadOnlyBecauseClosingCondition(editTaskStore.task?.attributes?.loanTaskId)
  }, []);

  const fetchOptions = useMemo(
    () => debounce(searchText => store.fetchOptions(searchText), 500),
    [store]
  );


  const taskTypeDisabled = (isReadOnly || isEscalationTask || store.isClosingConditionActive);

  const taskTypeFilteredOptions = [{ label: 'None', value: '' }, ...taskTypeOptions.filter(opt => {
    if (taskTypeDisabled) return true;//if this field is not editable show all types to display the value properly
    if (opt.legacy) return false;
    if (appraisalTaskTypes.includes(opt.value)) return false;
    if (opt.value === 'ESCALATION') return isEscalationTask;
    return true;
  })];

  return (
    <Grid container spacing={2}>
      {((supportTasksStore.openModal && DialogState.EDIT === dialogState) || !supportTasksStore.openModal) &&
        <Grid item xs={12}>
          <TaskStatusField
            label={'Status'}
            value={store.form.fields.status.value}
            onChange={(e, value) => store.onFieldChange('status', value)}
            options={generalTaskStatusEditOptions}
            disabled={isReadOnly || isLoanView || store.isClosingConditionActive || (DialogState.EDIT === dialogState && !isGeneralTask)}
          />
        </Grid>
      }
      <Grid item xs={12}>
        <TaskPriorityField
          label={'Priority'}
          value={store.form.fields.priority.value}
          onChange={(e, value) => store.onFieldChange('priority', value)}
          options={generalTaskPriorityOptions}
          disabled={isEscalationTask || store.isClosingConditionActive}
        />
      </Grid>
      {!supportTasksStore.openModal &&
        <Grid item xs={12}>
          <TaskAssigneeField
            label={'Assignee'}
            required={store.form.fields.assigneeRequired.value}
            value={store.form.fields.assigneeId.value}
            onChange={(e, value) => store.onFieldChange('assigneeId', value)}
            assigneeOptions={assigneeOptions}
            //disabled={isReadOnly}
            disabled={false}
            error={store.form.fields.assigneeId.error}
          />
        </Grid>
      }
      <Grid item xs={12}>
        {createSelectField({
          store,
          testId: 'category',
          fieldName: 'category',
          label: 'Category',
          disabled: DialogState.EDIT === dialogState,
          options: taskTypeFilteredOptions
        })}
      </Grid>
      {!supportTasksStore.openModal &&
        <Grid item xs={12}>
          {createDateField({
            store,
            testId: 'dueDate',
            fieldName: 'dueDate',
            label: 'Due Date',
          })}
        </Grid>
      }
      {!supportTasksStore.openModal &&
        <Grid item xs={12}>
          <TaskWatchField
            label={'Watchers'}
            value={store.form.fields.watchIds.value || []}
            onChange={(event, value) => store.onFieldChange('watchIds', value)}
            assigneeOptions={watcherOptions}
            disabled={false} />
        </Grid>
      }

      <Grid item xs={12}>
        <NumericAutocompleteField
          label="Loan Id"
          placeHolder="Select a Loan Id"
          value={store.form.fields.loanId.value}
          options={store.loanSearchOptions}
          onChange={text => {
            fetchOptions(text);
          }}
          onSelect={opt => {
            store.onFieldChange("loanId", opt.value);
          }}
          disabled={DialogState.EDIT === dialogState}
        />
      </Grid>
      {DialogState.EDIT === dialogState && !supportTasksStore.openModal &&
        <Grid item xs={12}>
          {createTextField({
            store,
            testId: 'ownerName',
            fieldName: 'ownerName',
            label: 'Reporter',
            disabled: true,
          })}
        </Grid>
      }

      {DialogState.EDIT === dialogState && (
        <>
          <Grid item xs={12}>
            <Typography>
              Created: {formatDate(taskV2?.createdDate)}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              Updated: {formatDate(taskV2?.lastUpdatedDate)}
            </Typography>
          </Grid>
          {assigneeStartDate && (
            <Grid item xs={12}>
              <Typography>
                Assigned Date: {formatDate(assigneeStartDate)}
              </Typography>
            </Grid>
          )}
          {taskV2?.taskTags.find(tag => tag.tagType === 'ESCALATED') && (
            <Grid item xs={12}>
              <Typography>
                Escalated Date: {formatDate(taskV2?.taskTags.find(tag => tag.tagType === 'ESCALATED').lastUpdatedDate)}
              </Typography>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
});
